import { Course } from "./Course";
import { KLASS_MODALITY, KLASS_TYPE } from "./Enums";
import { Klass, PrivateKlass } from "./Klass";

/**
 * Whether a class was delivered the way its course says it should have been.
 *
 * A PORT OF THE SERVICE'S `DeliveryPatternRules` AND `WorkingWeek`, and deliberately a faithful one:
 * the same question decides the `Non standard delivery pattern` flag, so a page answering it its own
 * way would nag about classes the service is content with and stay quiet on the ones it flags. A
 * change to the rule belongs in both places.
 *
 * A standard delivery is N consecutive working days, N being what the class is sold as: the days the
 * customer bought for a private class, otherwise the course's duration, with a bundle read through
 * its halves.
 *
 * WHERE IT DIFFERS FROM THE SERVICE, which is in one place only: `toleratedExtraDays` caps the
 * virtual stretch at courses shorter than a full week, and the service's does not. So the page asks
 * for a correction on a five day virtual class spanning six working days, which the flag leaves
 * alone; everything else the two agree on.
 */

/** The extra day a virtual class may stretch over - see `toleratedExtraDays`. */
const VIRTUAL_STRETCH_DAYS = 1;

/** A course this long fills the working week, so there is nowhere for it to stretch to. */
const FULL_WEEK_DAYS = 5;

const DAY_MS = 86_400_000;

/**
 * A cap on the day-by-day count below, so a class carrying a nonsense date cannot lock up the tab.
 * Far beyond any real delivery: the longest in the data is measured in months.
 */
const MAX_SPAN_DAYS = 4_000;

/** A window a country rested on Friday and Saturday in: `from` inclusive, `to` exclusive. */
interface Period {
    /** "yyyy-MM-dd", or null for unbounded. */
    from: string | null;
    to: string | null;
}

/** Which countries teach Sunday to Thursday, and when. Build one with `parseWorkingWeek`. */
export interface WorkingWeek {
    /** Alpha-2, upper case, to the windows it rested on Friday and Saturday in. */
    fridaySaturdayWeekend: Record<string, Period[]>;
}

/**
 * What `WORKING_WEEK_FRIDAY_SATURDAY` is declared as in the service, for the accessor's fallback.
 *
 * Only reached when the parameters have not loaded or the service does not know the key, and it is
 * the service's own default either way - an admin's override is read from the parameter store and
 * wins over this. Kept in step with `ParameterKey.WORKING_WEEK_FRIDAY_SATURDAY`.
 */
export const WORKING_WEEK_FRIDAY_SATURDAY_DEFAULT = [
    'SA', 'JO', 'QA', 'KW', 'BH', 'OM', 'EG', 'IL', 'IQ', 'PS', 'SY', 'YE',
    'LY', 'SD', 'DZ', 'AF', 'BD', 'MV', 'AE::2022-01-01'
];

/**
 * Reads the parameter's entries, each `CC`, `CC:from` or `CC:from:to` - an ISO alpha-2 country code,
 * optionally bounded by an inclusive start date and an exclusive end date, either of which may be
 * left empty to mean unbounded. `AE::2022-01-01` is therefore "the UAE, until 2022-01-01".
 *
 * The bounds are the part a plain country list cannot express: the United Arab Emirates moved to a
 * Saturday and Sunday weekend on 1 January 2022, so the answer depends on the day being counted and
 * not on today.
 *
 * An unreadable entry is skipped rather than throwing, as the service skips it: the parameter is free
 * text an admin can edit, and the cost of dropping one entry is that its country falls back to Monday
 * to Friday.
 */
export const parseWorkingWeek = (entries: string[] | undefined): WorkingWeek => {
    const fridaySaturdayWeekend: Record<string, Period[]> = {};

    for (const entry of entries ?? []) {
        if (!entry?.trim()) continue;

        const parts = entry.trim().split(':');
        if (parts.length > 3 || !parts[0]?.trim()) continue;

        const country = parts[0].trim().toUpperCase();
        const from = isoBound(parts[1]);
        const to = isoBound(parts[2]);
        if (from === false || to === false) continue;

        (fridaySaturdayWeekend[country] ??= []).push({ from, to });
    }

    return { fridaySaturdayWeekend };
};

/** A bound as "yyyy-MM-dd", null when empty or absent, or false when it is not a date at all. */
const isoBound = (value: string | undefined): string | null | false => {
    if (value === undefined || !value.trim()) return null;

    const trimmed = value.trim();
    return /^\d{4}-\d{2}-\d{2}$/.test(trimmed) ? trimmed : false;
};

const observesFridaySaturdayWeekend = (
    alpha2: string | undefined | null,
    isoDate: string,
    workingWeek: WorkingWeek
): boolean => {
    if (!alpha2) return false;

    // "yyyy-MM-dd" compares as a string exactly as it compares as a date, which is the whole reason
    // the dates are carried in that form rather than parsed.
    return (workingWeek.fridaySaturdayWeekend[alpha2.toUpperCase()] ?? [])
        .some(period => (period.from === null || isoDate >= period.from)
            && (period.to === null || isoDate < period.to));
};

/**
 * Whether the class's country teaches on this day.
 *
 * Weekends only. Public holidays are not in it, here or in the service: the span is being compared
 * with a course duration that counts teaching days the same way.
 *
 * @param alpha2 the country the class is delivered in, or nothing when there is none to ask of -
 *     which is the common case rather than the odd one, a virtual class having no country. Those
 *     fall to the Monday to Friday default.
 */
export const isWorkingDay = (
    isoDate: string,
    alpha2: string | undefined | null,
    workingWeek: WorkingWeek
): boolean => isWorkingDayAt(isoMillis(isoDate), alpha2, workingWeek);

const isWorkingDayAt = (
    millis: number,
    alpha2: string | undefined | null,
    workingWeek: WorkingWeek
): boolean => {
    const day = new Date(millis).getUTCDay(); // 0 Sunday .. 6 Saturday

    if (observesFridaySaturdayWeekend(alpha2, millisIso(millis), workingWeek)) {
        return day !== 5 && day !== 6;
    }
    return day !== 6 && day !== 0;
};

/** The working days from `from` to `to`, both inclusive. Zero if the range is inverted. */
export const workingDaysBetween = (
    from: string,
    to: string,
    alpha2: string | undefined | null,
    workingWeek: WorkingWeek
): number => {
    const last = isoMillis(to);
    const first = isoMillis(from);
    if (Number.isNaN(first) || Number.isNaN(last)) return 0;

    let days = 0;
    let counted = 0;
    for (let day = first; day <= last && counted < MAX_SPAN_DAYS; day += DAY_MS, counted++) {
        if (isWorkingDayAt(day, alpha2, workingWeek)) days++;
    }
    return days;
};

/**
 * The day a moment falls on in a given zone, as "yyyy-MM-dd".
 *
 * The zone is what makes this more than a formatting detail: a class starting at 23:00 UTC is already
 * the next day in Tokyo, and counting it on the wrong day moves the whole span. UTC when the country
 * has no zone, which is the reading the service takes.
 */
export const calendarDateIn = (value: Date | string, timeZone: string | undefined | null): string => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';

    // 'en-CA' is "yyyy-MM-dd". An unknown zone throws rather than falling back, so UTC catches it -
    // the timezone is editable in Administration and a typo must not take the page down.
    try {
        return formatIn(date, timeZone?.trim() || 'UTC');
    } catch {
        return formatIn(date, 'UTC');
    }
};

const formatIn = (date: Date, timeZone: string): string =>
    new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' })
        .format(date);

const isoMillis = (isoDate: string): number => {
    const [year, month, day] = isoDate.split('-').map(Number);
    if (!year || !month || !day) return NaN;
    return Date.UTC(year, month - 1, day);
};

const millisIso = (millis: number): string => new Date(millis).toISOString().slice(0, 10);

/**
 * How many days the class should have taken, or null when nothing says.
 *
 * Null is the honest answer for the `CU-*` custom courses, which carry no duration anywhere, being
 * bespoke engagements whose length is negotiated per customer. A class with no expectation cannot
 * fail to meet it, so `isNonStandardDelivery` answers false for them rather than flagging every
 * custom delivery there is.
 *
 * @param course the course to read the bundle halves from, for a caller holding a fuller copy than
 *     the one on the class - the catalogue's, which carries `bundleCourse.latestVersion`. Defaults
 *     to the class's own.
 */
export const expectedDeliveryDays = (klass: Klass, course?: Course | null): number | null => {
    if (!klass) return null;

    if (klass.type === KLASS_TYPE.PRIVATE) {
        const daysSold = (klass as PrivateKlass).daysSold;
        if (daysSold !== null && daysSold !== undefined) return daysSold;
    }

    const bundleHalf = bundleTeachingDays(course ?? klass.course);
    if (bundleHalf !== null) {
        // The teaching half alone, and NOT the exam day with it. A bundle's exam was either sat in
        // class, in which case the span holds it and runs a day longer, or taken on a voucher, in
        // which case it did not happen at the class at all - and the span is the only thing that
        // says which. Adding the exam day here asserts the first, and asks for a correction on
        // every in-person bundle that took the second as though a day of it were missing.
        return Math.ceil(bundleHalf);
    }

    const duration = klass.version?.duration;
    return duration === null || duration === undefined ? null : Math.ceil(duration);
};

/** The teaching half of a bundle, through its latest version, or null when this is not a bundle. */
const bundleTeachingDays = (course: Course | undefined | null): number | null => {
    if (!course?.isBundle || !course.bundleCourse) return null;

    const half = course.bundleCourse.latestVersion ?? course.bundleCourse.versions?.[0];
    return half?.duration ?? null;
};

/** The working days the class's dates span, read in the zone of the country it is delivered in. */
export const spanWorkingDays = (klass: Klass, workingWeek: WorkingWeek): number => {
    if (!klass?.startDateTime || !klass.endDateTime) return 0;

    const zone = klass.country?.timezone;
    return workingDaysBetween(
        calendarDateIn(klass.startDateTime, zone),
        calendarDateIn(klass.endDateTime, zone),
        klass.country?.alpha2,
        workingWeek
    );
};

/**
 * How far past its expected days a class may run and still be a standard delivery.
 *
 * ONE DAY FOR A VIRTUAL CLASS, AND NOTHING FOR ANYTHING ELSE. A course is written up as eight hour
 * days, and virtual delivery used to run at six, so the same material took a day longer: a four day
 * course taught virtually runs Monday to Friday, and that is standard rather than scattered. A class
 * taught in a room has no such stretch, so its dates are expected to say exactly what it was sold as.
 *
 * NOT FOR A FULL WEEK'S COURSE. Five days is already the whole working week at eight hours a day, so
 * there is no sixth day for it to spill into - a five day class spanning six working days has really
 * been spread across a calendar, and tolerating that would hide the case this exists to find.
 */
export const toleratedExtraDays = (klass: Klass, expected: number): number =>
    klass?.modality === KLASS_MODALITY.VIRTUAL && expected < FULL_WEEK_DAYS ? VIRTUAL_STRETCH_DAYS : 0;

/**
 * Whether the class's dates cannot account for the days it was sold as - its span holding fewer
 * working days than it was sold as, or more than a virtual stretch too many.
 *
 * It allows a virtual class the extra day it is stretched over - see `toleratedExtraDays` - and
 * nothing else: a bundle's in-room exam day is already counted into the expectation rather than
 * tolerated on top of it.
 *
 * BOTH DIRECTIONS MATTER, and for different reasons: a span that is too long is a delivery scattered
 * across a calendar, and a span that is too short is usually a private class whose `daysSold` has not
 * been filled in.
 *
 * RHLS IS NOT IN SCOPE. A session is three hours and the duration on it belongs to the multi-day
 * course it is hung off, so comparing the two would flag every RHLS class there is.
 *
 * It does not ask whether anyone has already recorded what happened - see `requiresDeliveredDays` for
 * the pairing that does, which is what makes the flag a work queue that empties.
 */
export const isNonStandardDelivery = (
    klass: Klass,
    workingWeek: WorkingWeek,
    course?: Course | null
): boolean => {
    if (!klass || klass.modality === KLASS_MODALITY.RHLS_PREMIUM
        || !klass.startDateTime || !klass.endDateTime) {
        return false;
    }

    const expected = expectedDeliveryDays(klass, course);
    if (expected === null) return false;

    const span = spanWorkingDays(klass, workingWeek);
    return span < expected || span > expected + toleratedExtraDays(klass, expected);
};

/**
 * Whether this class may not be saved until somebody says how many days it really took: its dates do
 * not describe the delivery, and nobody has corrected it yet.
 *
 * The same pair the service's flag rule asks, so a class the page insists on is exactly a class the
 * flag is raised against, and filling the field clears both at once.
 */
export const requiresDeliveredDays = (
    klass: Klass,
    workingWeek: WorkingWeek,
    course?: Course | null
): boolean =>
    klass?.deliveredDays === null || klass?.deliveredDays === undefined
        ? isNonStandardDelivery(klass, workingWeek, course)
        : false;

/**
 * What a typed delivered-days reads as: a non-negative number, or null for "not recorded".
 *
 * Blank clears the field, which is how a correction entered by mistake is taken back - and is also
 * what a number input hands back for anything it could not read, so an unparseable entry clears
 * rather than storing nonsense. A negative count is not a delivery, so it clears too.
 *
 * Halves survive on purpose: an RHLS session is three hours and counts half a day.
 */
export const parseDeliveredDays = (entered: unknown): number | null => {
    if (entered === null || entered === undefined) return null;

    const trimmed = String(entered).trim();
    if (!trimmed) return null;

    const parsed = Number(trimmed);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
};

/**
 * Whether an irregular delivery has already been recorded on this class.
 *
 * What the `Irregular delivery pattern` checkbox opens showing, and derived rather than stored: the
 * service holds these two fields and no flag beside them, so a box ticked with nothing behind it
 * would be a third state the service has no way to remember.
 */
export const hasRecordedDeliveryPattern = (klass: Klass): boolean =>
    (klass?.deliveredDays !== null && klass?.deliveredDays !== undefined)
    || (klass?.deliveryDays?.length ?? 0) > 0;
